import { doc, getDoc, Timestamp } from 'firebase/firestore';
import React, { useEffect, useState } from 'react'
import { twMerge } from 'tailwind-merge'
import { addComment, approveFighter, approveRegister, approveUpdateInfo, approveWeightChange, eventsCollection, fighterRequestsCollection, fightersCollection, updateFighterRequestStatus } from '../utils/firebase';
import { fighterRequestStatus, fighterRequestType, paymentMethod, weightClasses, weightClassesName } from '../constants';
import { extractTimestamp, formatFirestoreTimestamp, formatFirestoreTimestampToTime, splitCamel } from '../utils/common';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import Preview from '../components/preview';
import FighterRecordsTable from '../components/fighter-records-table';
import { ArrowRightFromLineIcon, Send, X } from 'lucide-react';
import { useAuth } from '../contexts/auth-context';

export default function FighterRequest() {
  const navigate = useNavigate()
  const { id } = useParams();
  const [requestId, setRequestId] = useState(id);
  const [request, setRequest] = useState(null);
  const [fighter, setFighter] = useState(null)
  const [event, setEvent] = useState(null)
  const [newComment, setNewComment] = useState("");
  // On a WEIGHT_CHANGE, approving always writes the registration. Whether the
  // fighter's profile weight follows is a separate, explicit choice — the same
  // one the manage-fighter save prompt offers.
  const [applyWeightToProfile, setApplyWeightToProfile] = useState(true);
  const { currentUser } = useAuth();

  const handleClose = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  }

  const handleAddComment = async () => {
    if (!newComment || newComment.trim() === "") {
      return toast.error("Please write something.");
    }
    if (!currentUser.uid) {
      return toast.error("Something went wrong please try to login again.");
    }

    await toast.promise(addComment(requestId, newComment, currentUser.uid), {
      loading: "Submitting..",
      success: () => {
        setRequest((prev) => {
          return {
            ...prev,
            comments: [
              ...prev.comments ?? [],
              {
                text: newComment,
                createdAt: Timestamp.now(),
                createBy: currentUser.uid
              }
            ]
          }
        })
        return "DONE"
      },
      error: (error) => {
        console.error(error);
        return "Something went wrong"
      },
    })
  }



  const handleApprove = () => {
    try {
      toast.promise((async () => {

        let result;

        if (request.type === fighterRequestType.newRegister) {
          result = await approveFighter({
            requestId: requestId
          })
        }

        if (request.type === fighterRequestType.registerEvent) {
          result = await approveRegister({
            requestId: requestId
          })
        }

        if (request.type === fighterRequestType.updateInfo) {
          result = await approveUpdateInfo({
            requestId: requestId
          })
        }

        // A weight change lands on the one registration it names. The admin
        // decides separately whether the fighter's profile weight follows.
        if (request.type === fighterRequestType.weightChange) {
          result = await approveWeightChange({
            requestId: requestId,
            applyToProfile: applyWeightToProfile,
          })
        }

        if (!result.data) throw new Error("Something went wrong")
        if (result.data.error) throw new Error(result.data.error)
        if (result.data) {
          setRequest((prev) => {
            return {
              ...prev,
              status: fighterRequestStatus.approved,
              comments: [],
            }
          })
        }
      })(), {
        loading: "Sumitting.",
        success: "Submitted.",
        error: (error) => error?.message || "Something went wrong."
      })
    } catch (error) {
      console.error(error);

    }
  }

  const handleRejected = async () => {
    await updateFighterRequestStatus(requestId, fighterRequestStatus.rejected)
    setRequest((prev) => {
      return {
        ...prev,
        status: fighterRequestStatus.rejected
      }
    })
  }

  useEffect(() => {
    (async () => {
      if (!!requestId) {
        const requestSnapsnot = await getDoc(doc(fighterRequestsCollection, requestId));
        if (requestSnapsnot.exists()) {
          const fetchedRequest = requestSnapsnot.data()
          setRequest({
            ...fetchedRequest,
            id: requestSnapsnot.id,
          });

          if (fetchedRequest?.type === fighterRequestType.updateInfo || fetchedRequest?.type === fighterRequestType.registerEvent || fetchedRequest?.type === fighterRequestType.weightChange) {
            const fighterId = fetchedRequest?.data?.fighterId;
            if (!!fighterId) {
              const fighterSnapsnot = await getDoc(doc(fightersCollection, fighterId))
              if (fighterSnapsnot.exists()) {
                setFighter({
                  ...fighterSnapsnot.data(),
                  id: fighterSnapsnot.id,
                })
              } else {
                setFighter(null)
              }
            } else {
              setFighter(null)
            }
          }

          if (fetchedRequest?.type === fighterRequestType.registerEvent || fetchedRequest?.type === fighterRequestType.weightChange) {
            const eventId = fetchedRequest?.data?.eventId;

            if (!!eventId) {
              const eventSnapshot = await getDoc(doc(eventsCollection, eventId))
              if (eventSnapshot.exists()) {
                setEvent({
                  ...eventSnapshot.data(),
                  id: eventSnapshot.id,
                })
              } else {
                setEvent(null)
              }
            } else {
              setEvent(null)
            }



          }
        } else {
          setRequest({});
          navigate("/404");
        }
      } else {
        setRequest({});
        navigate("/404")
      }
    })()
  }, [requestId, navigate, request?.type]);


  if (!request) {
    return (
      <div className='page-size flex-center'>
        <div className='loading loading-md loading-bars' />
      </div>
    )
  }


  return (
    <div className='page-size h-[calc(100vh-2rem)] flex-center gap-4 p-4'>
      <div className='w-full flex flex-row items-center justify-start'>
        <p className='text-xl font-bold'>{request.type?.split("_")?.join(" ") ?? "FIGHTER REQUEST"}</p>
        <div className="flex-1" />
        <button className="btn btn-square btn-ghost" onClick={handleClose}>
          <X />
        </button>
      </div>
      <div className='w-full h-full overflow-auto flex-1 '>
        <div className='flex flex-col w-full gap-6'>
          <div className='flex flex-col md:flex-row gap-2 items-start md:items-center border-b pb-2 hidden md:flex'>
            <p className='font-bold min-w-[300px]'>Name</p>
            <p className='font-bold w-full'>Value</p>
          </div>

          {
            request.type === fighterRequestType.weightChange && (
              <div className='w-full border border-base-300 p-4 flex flex-col gap-3'>
                <div className='flex flex-row flex-wrap items-baseline gap-3'>
                  <span className='font-mono opacity-60 line-through'>
                    {request.data?.previousWeightClass
                      ? `${request.data.previousWeightClass} ${weightClassesName[request.data.previousWeightClass] ?? ""}`
                      : "not previously declared"}
                  </span>
                  <span>→</span>
                  <span className='font-mono font-bold text-lg'>
                    {request.data?.eventWeightClass ?? "?"}{" "}
                    {weightClassesName[request.data?.eventWeightClass] ?? ""}
                  </span>
                </div>
                {request.data?.previousWeightClass &&
                  request.data.previousWeightClass !== request.data?.eventWeightClass && (
                    <p className='text-sm text-warning'>
                      This moves the fighter between weight classes. If they are already matched,
                      the pairing is kept and the bout is flagged for rematch review — it is never
                      unmatched automatically.
                    </p>
                  )}
                <label className='flex flex-row items-center gap-3 cursor-pointer'>
                  <input
                    type='checkbox'
                    className='checkbox checkbox-sm'
                    checked={applyWeightToProfile}
                    onChange={(e) => setApplyWeightToProfile(e.target.checked)}
                  />
                  <span className='text-sm'>
                    Also update the fighter's profile weight
                    <span className='opacity-60'> — leave off to change this event only</span>
                  </span>
                </label>
              </div>
            )
          }

          {
            request.type === fighterRequestType.registerEvent && (
              <div className='flex flex-col md:flex-row gap-2 items-start md:items-center'>
                <div className='min-w-[200px]'></div>
                <div className='flex flex-row gap-4 items-center justify-start w-full'>
                  {
                    fighter?.fighterPicture && <img className='h-36' src={fighter.fighterPicture} alt="" />
                  }
                  {
                    fighter?.fighterPicture && event?.coverImage && <ArrowRightFromLineIcon />
                  }
                  {
                    event?.coverImage && <img className='h-36' src={event.coverImage} alt="" />
                  }
                </div>
              </div>
            )
          }
          {
            Object.keys(request?.data ?? {}).length < 1
              ? <p>Empty</p>
              : Object.keys(request?.data).map((key, index) => {
                const value = request.data[key] ?? "?"
                let header = null;
                let cell = null;

                if (["receipt", "fighterPicture"].includes(key)) {
                  if (key === "receipt" && (!value || value === "?")) {
                    cell = (
                      <span className="opacity-70 italic">No slip uploaded (paid by credit)</span>
                    );
                  } else if (key === "fighterPicture") {
                    cell = (
                      <Preview
                          className="h-[12rem] w-[12rem] overflow-hidden object-center"
                          src={value}
                          imgClassName="object-cover"
                          zoomable
                      />
                    );
                  } else {
                    // Receipt: show whole slip uncropped, clickable to enlarge
                    cell = (
                      <Preview
                          src={value}
                          zoomable
                      />
                    );
                  }
                }

                if (key === "paymentMethod") {
                  header = "Payment Method";
                  cell = (
                    <span className={twMerge(
                      "badge font-bold",
                      value === paymentMethod.token
                        ? "bg-yellow-300 text-yellow-900 border-yellow-400"
                        : "badge-success"
                    )}>
                      {value === paymentMethod.token ? "Paid by Credit" : "Paid by Slip"}
                    </span>
                  );
                }

                if (key === "tokenId") {
                  if (!value || value === "?") return null;
                  header = "Credit ID";
                  cell = <code className="text-xs">{value}</code>;
                }

                if (value instanceof Timestamp) {
                  cell = formatFirestoreTimestamp(value)
                }

                if (key === "records") {
                  cell = <FighterRecordsTable records={value} />
                }

                if (key === "gender") {
                  cell = ({
                    "M": "Male",
                    "F": "Female"
                  }[value] ?? "???")
                }

                if (key === "weightClass") {
                  cell = `${value}: ${weightClasses[value] ?? "???"}`
                }

                if (key === "height") {
                  cell = `${value} cm`
                }

                if (key === "entry" && request.type === fighterRequestType.registerEvent) {
                  cell = `${value}, W: ${fighter?.records?.[value]?.win || 0} L: ${fighter?.records?.[value]?.loss || 0} D: ${fighter?.records?.[value]?.draw || 0}`
                }

                if (key === "fighterId") {
                  header = "Fighter"
                  cell = (
                    <>
                      <p>{fighter?.name}</p>
                      <p className='font-normal opacity-80'>{request.authIdentifier}</p>
                    </>
                  ) ?? <div className="loading loading-bars" />
                }

                if (key === "eventId") {
                  header = "Event"
                  cell = event?.name ?? <div className="loading loading-bars" />
                }

                return (
                  <div key={index} className='flex flex-col md:flex-row gap-2 items-start md:items-center'>
                    <p className='font-bold min-w-[300px] text-nowrap'>
                      {header ?? splitCamel(key) ?? "?"}
                    </p>
                    <div className={twMerge('font-semibold w-full')}>
                      {
                        cell ?? `${value}`
                      }
                    </div>
                  </div>
                )
              })
          }

          <div className='flex flex-col md:flex-row gap-2 items-start md:items-center'>
            <p className='font-bold min-w-[300px]'>Comments</p>
            <div className='flex-center w-full'>
              <div className='w-full flex-center gap-4'>
                {
                  !request?.comments || request.comments.length < 1
                    ? <div className="w-full h-full flex-center border border-base-300 p-4">
                      <p>No Comments</p>
                    </div>
                    : (request.comments).map((comment, index) => {
                      const createdAt = !comment.createdAt ? null : extractTimestamp(comment.createdAt)
                      return (
                        <div className='w-full ' key={index}>
                          <div className="chat chat-start">
                            <div className="chat-bubble">
                              <p className=''>
                                {comment.text ?? ""}
                              </p>
                            </div>
                            <div className="chat-footer">
                              {
                                !!createdAt &&
                                <p className='text-sm opacity-80'>{createdAt.day} {createdAt.month} {createdAt.year}, {createdAt.hour}:{createdAt.minute}</p>
                              }
                            </div>
                          </div>
                        </div>
                      )
                    })
                }
                <div className='w-full flex flex-row gap-4'>
                  <input type="text" className='input input-bordered w-full' placeholder='Write your comment here...' value={newComment} onChange={(e) => setNewComment(e.target.value)} />
                  <button className='btn btn-square btn-outline' onClick={handleAddComment} disabled={request.status === fighterRequestStatus.approved}>
                    <Send />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="w-full divider m-0"></div>
      <div className='w-full flex flex-row gap-2 items-center'>
        <p className='font-bold'>

          <span className='font-normal'>status: </span>
          {
            !!request && (request?.status ?? "?")
          }
        </p>
        <div className="flex-1" />
        <button className="btn btn-error btn-sm btn-outline" onClick={handleRejected} disabled={request?.status !== fighterRequestStatus.pending}>
          REJECT
        </button>
        <button className={twMerge("btn btn-primary btn-sm", request?.status === fighterRequestStatus.approved && "!btn-disabled")} onClick={handleApprove} disabled={request?.status === fighterRequestStatus.approved}>
          APPROVE
        </button>
      </div>
    </div>
  )
}
